import React, { useEffect, useMemo, useState, memo } from "react";
import { Map } from "react-map-gl/mapbox";
import { FiHeadphones, FiMail, FiMapPin, FiChevronUp } from "react-icons/fi";
import ScrollToTop from "./common/ScrollToTop";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { ContactEntity, SponsorEntity } from "../types/strapi";
import { accessToken, siteUrl } from "../utils/constants";
import ContactTwo from "./contact/ContactTwo";
import BrandTwo from "./BrandTwo";
import RenderLoader from "./common/RenderLoader";
import PageHelmet from "./common/Helmet";
import Header from "./common/Header";
import Breadcrumb from "./common/Breadcrumb";
import FooterTwo from "./common/FooterTwo";
import { sanitizeContact } from "../utils/aboutUtils";
import { mediaUrl } from "../utils/imageUtils";

const settings = {
  dragPan: false,
  dragRotate: false,
  scrollZoom: false,
  keyboard: false,
  doubleClickZoom: false,
  width: "100%",
  height: "100%",
};

interface ContactProps {
  contact?: ContactEntity;
  sponsors?: SponsorEntity;
}

interface ContactDetailProps {
  icon: React.ReactElement;
  title: string;
  content: React.ReactNode;
}

interface ContactState {
  viewPort: {
    latitude?: number;
    longitude?: number;
    zoom?: number;
  };
  error: string | null;
}

const defaultContact = {} as ContactEntity;
const defaultSponsors = {} as SponsorEntity;

const Contact = ({
  contact = defaultContact,
  sponsors = defaultSponsors,
}: ContactProps) => {
  const sanitizedContact = useMemo(() => sanitizeContact(contact as unknown as Record<string, unknown>), [contact]);
  const backgroundSection = "Archive";
  const section = "Contact";
  const [contactState, setContactState] = useState<ContactState>({
    viewPort: {},
    error: null,
  });

  useEffect(() => {
    if (sanitizedContact.latitude && sanitizedContact.longitude) {
      setContactState((prevState) => ({
        ...prevState,
        viewPort: {
          latitude: sanitizedContact.latitude,
          longitude: sanitizedContact.longitude,
          zoom: sanitizedContact.mapZoom,
        },
        error: null, // Clear any previous error
      }));
    } else {
      setContactState((prevState) => ({
        ...prevState,
        error: "Error loading map: Latitude and longitude not provided.",
      }));
    }
  }, [sanitizedContact]);

  return (
    <>
      <PageHelmet pageTitle={section} />
      <Header />
      <Breadcrumb title={section} backgroundSection={backgroundSection} />
      <div className="rn-contact-top-area ptb--120 bg_color--5">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title mb--30 text-center">
                {sanitizedContact.superHeading ? (
                  <>
                    <span className="subtitle">
                      {sanitizedContact.superHeading}
                    </span>
                    <h3 className="title mt--20 mb--50">
                      {sanitizedContact.heading}
                    </h3>
                    <div className="text-start">
                      <BlocksRenderer content={sanitizedContact.text as any} />
                    </div>
                  </>
                ) : (
                  <RenderLoader />
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <ContactDetail
              icon={<FiHeadphones />}
              title="Contact Phone Number"
              content={
                <p>
                  <a href={`tel:${sanitizedContact.phone}`}>
                    {sanitizedContact.phone}
                  </a>
                </p>
              }
            />
            <ContactDetail
              icon={<FiMail />}
              title="Our Email Address"
              content={
                <p>
                  <a href={`mailto:${sanitizedContact.email}`}>
                    {sanitizedContact.email}
                  </a>
                </p>
              }
            />
            <ContactDetail
              icon={<FiMapPin />}
              title="Our Location"
              content={<BlocksRenderer content={sanitizedContact.address as any} />}
            />
          </div>
        </div>
      </div>
      <div className="rn-contact-page ptb--120 bg_color--1">
        {contact?.formText?.image ? (
          <ContactTwo
            caption={
              contact?.formText?.image?.[0]?.caption || ""
            }
            contactImg={mediaUrl(
              contact?.formText?.image?.[0]?.formats?.medium?.url || ""
            )}
            heading={contact?.formText?.heading || ""}
            email={contact?.email || ""}
            superHeading={contact?.formText?.superHeading || ""}
            phone={contact?.phone || ""}
          />
        ) : (
          <RenderLoader />
        )}
      </div>
      <div
        className="rn-contact-map-area position-relative"
        style={{ height: "650px", width: "100%" }}
      >
        {contactState.viewPort.latitude ? (
          <Map
            {...contactState.viewPort}
            mapboxAccessToken={accessToken}
            mapStyle="mapbox://styles/mapbox/outdoors-v11"
            onMove={(evt) =>
              setContactState((prevState) => ({
                ...prevState,
                viewPort: evt.viewState,
              }))
            }
            dragPan={settings.dragPan}
            dragRotate={settings.dragRotate}
            scrollZoom={settings.scrollZoom}
            keyboard={settings.keyboard}
            doubleClickZoom={settings.doubleClickZoom}
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          <p style={{ color: "red", textAlign: "center", marginTop: "20px" }}>
            {contactState.error}
          </p>
        )}
      </div>
      <BrandTwo sponsors={sponsors} />
      <div className="backto-top">
        <ScrollToTop showUnder={160}>
          <FiChevronUp />
        </ScrollToTop>
      </div>
      <FooterTwo />
    </>
  );
};

const ContactDetail = ({ icon, title, content }: ContactDetailProps) => (
  <div className="col-lg-4 col-md-6 col-sm-6 col-12">
    <div className="rn-address">
      <div className="icon">{icon}</div>
      <div className="inner">
        <h4 className="title">{title}</h4>
        {content}
      </div>
    </div>
  </div>
);

export default memo(Contact);
